import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Compass, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import AirportPicker from "./AirportPicker";

const today = () => new Date().toISOString().slice(0, 10);

export default function SearchBar({ defaultTab = "search", onSubmit }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(defaultTab);
  const [from, setFrom] = useState(params.get("from") || "JFK");
  const [to, setTo] = useState(params.get("to") || "LHR");
  const [date, setDate] = useState(params.get("date") || today());
  const [flex, setFlex] = useState(Number(params.get("flex") ?? 3));

  // Keep the form in sync when the URL changes (e.g. calendar day click, notification link)
  useEffect(() => {
    if (params.get("from")) setFrom(params.get("from"));
    if (params.get("to")) setTo(params.get("to"));
    if (params.get("date")) setDate(params.get("date"));
    if (params.get("flex") != null) setFlex(Number(params.get("flex")));
  }, [params]);

  const submit = () => {
    if (tab === "explore") { navigate("/explore"); return; }
    if (!from || !to) return;
    navigate(`/search?from=${from}&to=${to}&date=${date}&flex=${flex}`);
    onSubmit?.();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
      <div className="flex">
        <button onClick={() => setTab("search")} className={`flex flex-1 items-center justify-center gap-2 rounded-tl-2xl py-3.5 text-sm font-semibold transition-colors ${tab === "search" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <SearchIcon className="h-4 w-4" /> Search
        </button>
        <button onClick={() => setTab("explore")} className={`flex flex-1 items-center justify-center gap-2 rounded-tr-2xl py-3.5 text-sm font-semibold transition-colors ${tab === "explore" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Compass className="h-4 w-4" /> Explore
        </button>
      </div>
      <div className="p-4">
        {tab === "search" ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <AirportPicker label="From" value={from} onChange={setFrom} testId="search-from-input" className="flex-1" />
            <AirportPicker label="To" value={to} onChange={setTo} testId="search-to-input" className="flex-1" />
            <div className="grid grid-cols-2 gap-3 md:contents">
              <div className="md:flex-1">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Departure</label>
                <div className="flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                  <Calendar className="hidden h-4 w-4 text-slate-400 sm:block" />
                  <input type="date" value={date} min={today()} onChange={(e) => setDate(e.target.value)} data-testid="search-date-input" className="w-full min-w-0 bg-transparent text-base outline-none sm:text-sm" />
                </div>
              </div>
              <div className="md:w-32 md:shrink-0">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Flex</label>
                <select value={flex} onChange={(e) => setFlex(Number(e.target.value))} data-testid="search-flex-select" className="h-11 w-full rounded-lg border border-slate-200 bg-white px-2 text-base outline-none focus:border-blue-500 sm:text-sm">
                  {[0, 1, 2, 3, 5, 7].map((d) => <option key={d} value={d}>{`± ${d} days`}</option>)}
                </select>
              </div>
            </div>
            <Button onClick={submit} data-testid="search-submit-button" className="h-12 gap-2 bg-blue-600 px-6 text-base font-semibold hover:bg-blue-700 md:h-11 md:text-sm">
              <SearchIcon className="h-4 w-4" /> Search
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <p className="text-sm text-slate-600">Browse award availability from an entire region — no dates required.</p>
            <Button onClick={() => navigate("/explore")} className="gap-2 bg-blue-600 font-semibold hover:bg-blue-700"><Compass className="h-4 w-4" /> Open Explore</Button>
          </div>
        )}
      </div>
    </div>
  );
}
